import React from 'react';
import { cn } from '@/lib/utils';
import InstagramIcon from '@/assets/icons/social/Instagram.svg';
import LinkedinIcon from '@/assets/icons/social/Linkedin.svg';
import TiktokIcon from '@/assets/icons/social/Tiktok.svg';
import TwitterIcon from '@/assets/icons/social/Twitter.svg';
import YoutubeIcon from '@/assets/icons/social/Youtube.svg';
import { Image } from '@/pages/reports/components/ui';
import { HtmlContent } from '@/components/ui/html-content';
import type { TooltipItem } from '@/pages/reports/components/form/TooltipListInput';
import type { NewsSubsection } from '@/pages/reports/components/form/NewsSubsectionsInput';
import {
  resolveNewsSectionNameFromBadge,
  toSpecialNameSlug,
} from '@/lib/reportAnalyticsSections';
import { useReportI18n } from '@/pages/reports/i18n';

type SocialKey = 'instagram' | 'linkedin' | 'tiktok' | 'twitter' | 'youtube';

const SOCIAL_ICONS: Record<SocialKey, string> = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  tiktok: TiktokIcon,
  twitter: TwitterIcon,
  youtube: YoutubeIcon
};

type SocialItem = {
  type: SocialKey;
  link: string;
};

export interface NewsCardTooltipItem extends TooltipItem {}

const COUNTRY_FLAG: Record<string, string> = {
  'LATAM': '🌎',
  'Global': '🌍',
  'Brasil': '🇧🇷',
  'Argentina': '🇦🇷',
  'México': '🇲🇽',
  'Colômbia': '🇨🇴',
  'Chile': '🇨🇱',
  'Peru': '🇵🇪',
  'Uruguai': '🇺🇾',
  'Paraguai': '🇵🇾',
  'Bolívia': '🇧🇴',
  'Equador': '🇪🇨',
  'Venezuela': '🇻🇪',
};

interface NewsCardProps {
  badgeLabel: string;
  badgeLabelCustom?: string;
  socialList: SocialItem[];
  coverImage1?: string;
  coverImage2?: string;
  coverImage3?: string;
  coverImage4?: string;
  title: string;
  titleLink?: string;
  subsections?: NewsSubsection[];
  subtitle?: string;
  tooltips?: NewsCardTooltipItem[];
  tooltipTitleWord?: string;
  tooltipTitleWordBackgroundColor?: string;
  tooltipTitleWordTextColor?: string;
  tooltipItems?: string[];
  tooltipBackgroundColor?: string;
  tooltipTextColor?: string;
  className?: string;
}

function normalizeLegacyTooltips(props: NewsCardProps): NewsCardTooltipItem[] {
  if (props.tooltips && Array.isArray(props.tooltips) && props.tooltips.length > 0) {
    return props.tooltips;
  }
  if (props.tooltipItems?.length || props.tooltipTitleWord != null) {
    return [
      {
        titleMode: 'ooooi',
        ooooiPrefix: 'ooooi',
        titleWord: props.tooltipTitleWord,
        titleWordBackgroundColor: props.tooltipTitleWordBackgroundColor,
        titleWordTextColor: props.tooltipTitleWordTextColor,
        items: props.tooltipItems ?? [],
        backgroundColor: props.tooltipBackgroundColor ?? '#F2FF00',
        textColor: props.tooltipTextColor ?? '#000000',
      },
    ];
  }
  return [];
}

function normalizeSubsections(props: NewsCardProps): NewsSubsection[] {
  if (props.subsections && props.subsections.length > 0) {
    return props.subsections;
  }
  const legacyTooltips = normalizeLegacyTooltips(props);
  if (props.subtitle || legacyTooltips.length > 0) {
    return [{ content: props.subtitle, tooltips: legacyTooltips }];
  }
  return [];
}

function TooltipBalloon({
  tooltip,
  sectionName,
  specialName,
}: {
  tooltip: NewsCardTooltipItem;
  sectionName?: string;
  specialName?: string;
}) {
  const { t } = useReportI18n();
  const mode = tooltip.titleMode ?? 'ooooi';
  const bgColor = (tooltip.backgroundColor?.trim() || (mode === 'como-usar' ? '#F5F5F5' : '#F2FF00'));
  const textColor = (tooltip.textColor?.trim() || '#000000');
  const wordBgColor = (tooltip.titleWordBackgroundColor?.trim() || '#ffffff');
  const wordTextColor = (tooltip.titleWordTextColor?.trim() || '#000000');

  const hasContent =
    (tooltip.content != null && tooltip.content.trim() !== '') ||
    (tooltip.items && tooltip.items.length > 0);

  const hasTitle =
    mode === 'como-usar' ||
    (mode === 'custom' && tooltip.titleCustom?.trim()) ||
    (mode === 'ooooi' && (
      (tooltip.ooooiPrefix ?? 'ooooi').trim() !== '' ||
      tooltip.titleWord?.trim()
    ));

  if (!hasTitle && !hasContent) return null;

  return (
    <div
      className="relative px-6 py-5 flex flex-col gap-4"
      style={{ backgroundColor: bgColor, color: textColor }}
      data-report-section-name={sectionName}
      data-report-special-name={specialName}
      data-report-actionable-insight={
        sectionName === 'trends' || sectionName === 'formatos' ? 'true' : undefined
      }
    >
      <div
        className="absolute left-8 top-0 w-0 h-0 border-[10px] border-transparent border-t-0"
        style={{ transform: 'translateY(-100%)', borderBottomColor: bgColor }}
        aria-hidden
      />

      <div className="flex items-center flex-wrap gap-2 font-['Times_New_Roman'] font-normal text-[12px] leading-[100%] tracking-[0%] align-middle">
        {mode === 'como-usar' && (
          <span>{t('news.howToUse')}</span>
        )}
        {mode === 'custom' && tooltip.titleCustom && (
          <span>{tooltip.titleCustom} 👀</span>
        )}
        {(mode === 'ooooi' || (!tooltip.titleMode)) && (
          <>
            <span>{tooltip.ooooiPrefix ?? 'ooooi'} </span>
            {tooltip.titleWord ? (
              <span
                className="rounded px-1.5 py-0.5 align-middle"
                style={{ backgroundColor: wordBgColor, color: wordTextColor }}
              >
                {tooltip.titleWord}
              </span>
            ) : null}
            <span> 👀</span>
          </>
        )}
      </div>

      {hasContent && (
        <div className="text-[12px] [&_*]:!text-inherit">
          {tooltip.content != null && tooltip.content.trim() !== '' ? (
            <HtmlContent
              html={tooltip.content}
              styleOptions={{ pDisplay: 'block', headingsDisplay: 'block' }}
            />
          ) : (tooltip.items ?? []).length > 0 ? (
            (tooltip.items ?? []).map((item, i) => (
              <div key={i} className="mb-2 last:mb-0">
                <HtmlContent
                  html={item}
                  styleOptions={{ pDisplay: 'inline', headingsDisplay: 'block' }}
                />
              </div>
            ))
          ) : null}
        </div>
      )}
    </div>
  );
}

export const NewsCard: React.FC<NewsCardProps> = (props) => {
  const { t } = useReportI18n();
  const {
    badgeLabel,
    badgeLabelCustom,
    socialList,
    coverImage1,
    coverImage2,
    coverImage3,
    coverImage4,
    title,
    titleLink,
    className,
  } = props;

  const displayBadge = badgeLabel === 'Outro' && badgeLabelCustom?.trim()
    ? badgeLabelCustom.trim()
    : t(`news.badges.${badgeLabel}`, badgeLabel);

  const images = [coverImage1, coverImage2, coverImage3, coverImage4]
    .map((img) => (img ?? '').trim())
    .filter(Boolean)
    .slice(0, 4) as string[];

  const subsections = normalizeSubsections(props);
  const sectionName = resolveNewsSectionNameFromBadge(badgeLabel);
  const specialName = toSpecialNameSlug(title);

  return (
    <section className={cn('w-full overflow-x-hidden bg-white text-black', className)}>
      <div className="container mx-auto px-4 md:px-0">
        <div className="bg-white max-w-[500px] mx-auto py-8 md:py-10 flex flex-col gap-8 min-w-0">

          <header className="flex flex-wrap items-center gap-3">
            <span className="px-4 py-1 rounded-full bg-[#F2FF00] text-xs">
              {displayBadge}
            </span>
            <div className="flex items-center justify-start gap-2">
              {(socialList || []).map(({ type, link }) => {
                const icon = SOCIAL_ICONS[type];
                if (!icon) return null;
                const href = (link ?? '').trim();
                if (!href) {
                  return (
                    <span key={type} className="w-6 h-6 inline-flex" aria-label={type}>
                      <img src={icon} alt={type} className="w-6 h-6" />
                    </span>
                  );
                }
                return (
                  <a
                    key={type}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-6 h-6"
                    aria-label={type}
                  >
                    <img src={icon} alt={type} className="w-6 h-6" />
                  </a>
                );
              })}
            </div>
          </header>

          {images.length > 0 &&
            (images.length === 1 ? (
            <div className="w-full max-w-[250px] mx-auto aspect-square overflow-hidden">
              <Image src={images[0]} alt="" className="w-full h-full object-cover object-center" />
            </div>
          ) : images.length === 2 ? (
            <div className="grid grid-cols-2 w-full gap-0 min-w-0">
              {images.map((image, index) => (
                <div key={index} className="aspect-square w-full min-w-0 overflow-hidden">
                  <Image src={image} alt="" className="w-full h-full object-cover object-center" />
                </div>
              ))}
            </div>
          ) : images.length === 3 ? (
            <div className="grid grid-cols-2 grid-rows-2 w-full aspect-square gap-0 min-w-0">
              <div className="row-span-2 min-w-0 overflow-hidden">
                <Image src={images[0]} alt="" className="w-full h-full object-cover object-center" />
              </div>
              <div className="min-w-0 overflow-hidden">
                <Image src={images[1]} alt="" className="w-full h-full object-cover object-center" />
              </div>
              <div className="min-w-0 overflow-hidden">
                <Image src={images[2]} alt="" className="w-full h-full object-cover object-center" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 w-full gap-0 min-w-0">
              {images.map((image, index) => (
                <div key={index} className="aspect-square w-full min-w-0 overflow-hidden">
                  <Image src={image} alt="" className="w-full h-full object-cover object-center" />
                </div>
              ))}
            </div>
          ))}

          <div className="flex flex-col gap-2 min-w-0">
            <h2 className="font-normal text-[48px] leading-[52px] font-['Times_New_Roman'] break-words">
              {titleLink ? (
                <a href={titleLink} target="_blank" rel="noopener noreferrer" className="underline">
                  {title}
                </a>
              ) : (
                title
              )}
            </h2>
          </div>

          {subsections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="flex flex-col gap-6 min-w-0">
              {section.countries && section.countries.length > 0 && (
                <div className="flex flex-wrap gap-1.5 text-xl">
                  {section.countries.map((c) => (
                    <span key={c} title={c}>
                      {COUNTRY_FLAG[c] ?? c}
                    </span>
                  ))}
                </div>
              )}

              {section.content && section.content.trim() !== '' && (
                <HtmlContent
                  html={section.content}
                  className="text-[14px] text-gray-700 leading-relaxed"
                />
              )}

              {(section.tooltips ?? []).map((tooltip, tooltipIndex) => (
                <TooltipBalloon
                  key={tooltipIndex}
                  tooltip={tooltip}
                  sectionName={sectionName}
                  specialName={specialName}
                />
              ))}
            </div>
          ))}

        </div>
      </div>
    </section>
  );
};
